"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, TrendingUp, ArrowRight, ShieldCheck, Star, Search, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustPoints = [{
  id: 1,
  text: "Direct consultant access — you work 100% directly with me, Yassine"
}, {
  id: 2,
  text: "Hyper-targeted Google Maps & Hull local search optimization"
}, {
  id: 3,
  text: "Transparent reporting & measurable organic revenue growth"
}];
const localMetrics = [{
  id: 1,
  value: "#1",
  label: "Local Map Pack",
  description: "Average client goal"
}, {
  id: 2,
  value: "+210%",
  label: "Organic Traffic",
  description: "Verified local search lift"
}, {
  id: 3,
  value: "100%",
  label: "Independent Expert",
  description: "Zero agency middle-men"
}];
export default function Localseo() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="localseoherohull" data-nav="dark" className="relative min-h-[85vh] flex items-center bg-primary text-primary-foreground pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-accent/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-0 w-96 h-96 bg-accent/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div className="lg:col-span-7 flex flex-col space-y-6 text-left" initial="hidden" animate="visible" transition={{
          duration: 0.6,
          ease: "easeOut"
        }} variants={fadeInVariants}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-accent/20 text-accent-foreground border border-accent/40 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full inline-flex items-center gap-1.5">
                <MapPin className="size-3.5 text-accent" />
                <span>Hull &amp; East Yorkshire</span>
              </Badge>
              <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground px-3 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1.5 bg-primary-foreground/10">
                <Sparkles className="size-3.5 text-accent" />
                <span>Independent SEO Consultancy</span>
              </Badge>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.12]">
              Local SEO Consultant in{" "}
              <span className="text-accent underline decoration-accent/40 underline-offset-8">
                Hull
              </span>
            </h2>

            <p className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help businesses across Hull, Humber, and East Yorkshire capture high-intent local customers on Google Search and Maps. When you partner with YE Consultancy, you work directly with me — Yassine Essaadi — delivering tailored, sustainable search growth without agency fluff.
            </p>

            <div className="space-y-3 py-2">
              {trustPoints.map((point, i) => <div key={point.id} data-index={i} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-primary-foreground/95 font-medium">
                    {point.text}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-lg shadow-md px-6 py-3 h-auto text-base">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium rounded-lg px-6 py-3 h-auto text-base bg-transparent">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>

            <div className="pt-4 border-t border-primary-foreground/15 flex items-center gap-4">
              <div className="flex -space-x-2">
                <SafeHydrate><Avatar className="border-2 border-primary size-9">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_1.png" alt="Satisfied client avatar" />
                  <AvatarFallback className="bg-accent text-accent-foreground font-bold text-xs">
                    YE
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <SafeHydrate><Avatar className="border-2 border-primary size-9">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_2.png" alt="Verified client review" />
                  <AvatarFallback className="bg-accent/80 text-accent-foreground font-bold text-xs">
                    SEO
                  </AvatarFallback>
                </Avatar></SafeHydrate>
              </div>
              <div className="text-xs sm:text-sm text-primary-foreground/90">
                <div className="flex items-center gap-1 text-accent">
                  <Star className="size-3.5 fill-current" />
                  <Star className="size-3.5 fill-current" />
                  <Star className="size-3.5 fill-current" />
                  <Star className="size-3.5 fill-current" />
                  <Star className="size-3.5 fill-current" />
                </div>
                <span className="font-semibold text-primary-foreground">
                  Direct Expert Oversight
                </span>{" "}
                • Tailored Strategies for UK Businesses
              </div>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-5 relative" initial="hidden" animate="visible" transition={{
          duration: 0.7,
          delay: 0.15,
          ease: "easeOut"
        }} variants={fadeInVariants}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative rounded-2xl overflow-hidden bg-card text-card-foreground border border-border shadow-2xl">
                <div className="relative aspect-[4/5] sm:aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent Local SEO Consultant in Hull" className="w-full h-full object-cover object-center" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-serif text-lg font-bold">
                          Yassine Essaadi
                        </p>
                        <p className="text-xs text-white/80">
                          Senior SEO Strategy Consultant
                        </p>
                      </div>
                      <Badge className="bg-accent text-accent-foreground text-xs px-2.5 py-1 font-semibold rounded-md">
                        YE Consultancy
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-card border-t border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-md bg-primary/10 text-primary">
                      <Search className="size-4" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-card-foreground">
                        Google Local Search Hull
                      </p>
                      <p className="text-[13px] text-muted-foreground">
                        Map Pack &amp; Organic Rankings
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="p-2.5 rounded-lg bg-muted/60 flex items-center justify-between text-xs">
                      <span className="font-medium text-foreground flex items-center gap-1.5">
                        <MapPin className="size-3.5 text-primary" />
                        Your Hull Business
                      </span>
                      <span className="text-emerald-600 font-bold flex items-center gap-1">
                        <TrendingUp className="size-3.5" />
                        Rank #1
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-card text-card-foreground p-4 rounded-xl shadow-xl border border-border flex items-center gap-3.5 max-w-[220px]">
                <div className="p-2.5 rounded-lg bg-accent/20 text-accent shrink-0">
                  <ShieldCheck className="size-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-card-foreground">
                    Direct Access
                  </p>
                  <p className="text-[13px] text-muted-foreground leading-tight">
                    No account managers or agency middle-men
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-16 pt-8 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {localMetrics.map((metric, i) => <div key={metric.id} data-index={i} className="bg-primary-foreground/5 rounded-xl p-5 border border-primary-foreground/10 text-center sm:text-left">
              <div className="font-serif text-3xl lg:text-4xl font-bold text-accent mb-1">
                {metric.value}
              </div>
              <div className="text-sm font-semibold text-primary-foreground">
                {metric.label}
              </div>
              <div className="text-xs text-primary-foreground/70 mt-0.5">
                {metric.description}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}